import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PerkCard } from './PerkCard';
import type { Perk } from '../types/eventBookingCategoryTypes';

interface PerkCarouselProps {
  perks: Perk[];
}

export const PerkCarousel: React.FC<PerkCarouselProps> = ({ perks }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const overflowing = scrollWidth > clientWidth;
      setShowLeftArrow(overflowing && scrollLeft > 10);
      setShowRightArrow(overflowing && scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      // Initial check with a slight delay to ensure layout is ready
      const timeout = setTimeout(checkScroll, 100);
      // Also check on resize
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        clearTimeout(timeout);
      };
    }
  }, [perks]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth / 2 : clientWidth / 2;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="w-full mt-4">
      <div className="px-5 mb-4 text-left">
        <h2 className="text-xl md:text-2xl font-bold font-serif text-white tracking-tight">Exclusive Perks</h2>
      </div>
      
      <div className="group relative w-full px-5">
        {/* Desktop Navigation Arrows (hidden on md and smaller) */}
        <div className="hidden md:block">
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-7 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-white/10 text-white backdrop-blur-md hover:bg-black/80 transition-all shadow-xl"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-7 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/60 border border-white/10 text-white backdrop-blur-md hover:bg-black/80 transition-all shadow-xl"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Scrollable Container */}
        <div
          ref={scrollRef}
          className="overflow-x-auto pb-4 scrollbar-hide scroll-smooth no-scrollbar w-full"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="flex gap-3 sm:gap-4 w-max mx-auto">
            {perks.map((perk, index) => (
              <PerkCard key={index} perk={perk} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
